import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { formatPayMonthLabel } from "./runPayrollUtils";

/**
 * Pick up to 3 finalised pay months to compare against current month.
 */
export default function PayrollReconciliationExportModal({
  darkMode,
  open,
  onClose,
  onConfirm,
  exporting = false,
  currentPayMonth = "",
  availableFinalisedMonths = [],
  defaultCompare = [],
}) {
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open) return;
    const initial =
      Array.isArray(defaultCompare) && defaultCompare.length
        ? defaultCompare.slice(0, 3)
        : availableFinalisedMonths[0]
          ? [availableFinalisedMonths[0].payMonth]
          : [];
    setSelected(initial);
  }, [open, defaultCompare, availableFinalisedMonths]);

  if (!open) return null;

  const shell = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const btn = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:border-[hsl(var(--ds-primary))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:border-[hsl(var(--ds-primary))]";

  const toggle = (ym) => {
    setSelected((prev) => {
      if (prev.includes(ym)) return prev.filter((m) => m !== ym);
      if (prev.length >= 3) return prev;
      return [...prev, ym];
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className={`flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border shadow-xl ${shell}`}>
        <div
          className={`flex items-center justify-between border-b px-4 py-3 ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <div>
            <h3 className="text-sm font-semibold">Payroll Reconciliation (Reco Report)</h3>
            <p className={`text-[11px] ${muted}`}>
              Current: {formatPayMonthLabel(currentPayMonth)} · up to 3 finalised months · exports Reco
              Report with reasons
            </p>
          </div>
          <button type="button" onClick={onClose} className={`rounded p-1 ${btn}`}>
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3 text-xs">
          <p className={muted}>
            Default is the previous finalised month. You can select up to 3 finalised months for
            comparison.
          </p>
          {!availableFinalisedMonths.length ? (
            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              No finalised previous months found. Finalise an earlier payroll month first.
            </p>
          ) : (
            <ul
              className={`max-h-64 overflow-y-auto rounded border ${
                darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
              }`}
            >
              {availableFinalisedMonths.map((m) => {
                const ym = m.payMonth;
                const checked = selected.includes(ym);
                const disabled = !checked && selected.length >= 3;
                return (
                  <li
                    key={ym}
                    className={`flex items-center gap-2 border-b px-3 py-2 last:border-b-0 ${
                      darkMode ? "border-[#333]" : "border-[hsl(var(--ds-border))]"
                    } ${disabled ? "opacity-40" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(ym)}
                      className="h-3.5 w-3.5"
                      id={`recon-${ym}`}
                    />
                    <label htmlFor={`recon-${ym}`} className="flex-1 cursor-pointer">
                      <span className="font-medium">{formatPayMonthLabel(ym)}</span>
                      <span className={`ml-2 ${muted}`}>{ym}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <p className={`tabular-nums ${muted}`}>Selected: {selected.length} / 3</p>
        </div>

        <div
          className={`flex flex-wrap items-center justify-end gap-2 border-t px-4 py-3 ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <button type="button" onClick={onClose} className={`rounded border px-3 py-1.5 text-xs ${btn}`} disabled={exporting}>
            Cancel
          </button>
          <button
            type="button"
            disabled={exporting || !selected.length}
            onClick={() => onConfirm?.(selected)}
            className="inline-flex items-center gap-1.5 rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : null}
            Export Excel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
